import { useState, useEffect } from 'react'
import { Button, Input, Label, Select, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Badge } from '../components/ui'
import { useCompany } from '../hooks/useCompany'
import { useAuth } from '../hooks/useAuth'
import { Plus, Mail, Phone, Copy, Check } from 'lucide-react'

interface Invitation {
  id: string
  contact: string
  contact_type: 'email' | 'phone'
  role: string
  status: 'pending' | 'accepted' | 'expired'
  inviter_name: string
  token: string
  expires_at: number
  created_at: number
  accepted_at?: number
}

export default function Invitations() {
  const { currentCompany } = useCompany()
  const { } = useAuth()
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [copiedToken, setCopiedToken] = useState<string | null>(null)
  const [newInvitation, setNewInvitation] = useState({
    contact: '',
    type: 'email' as 'email' | 'phone',
    role: 'MEMBER'
  })

  // Fetch invitations
  useEffect(() => {
    if (!currentCompany?.id) {
      setLoading(false)
      return
    }

    fetchInvitations()
  }, [currentCompany?.id])

  const fetchInvitations = async () => {
    if (!currentCompany?.id) return

    try {
      setLoading(true)
      setError(null)

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/invitations?team_id=${currentCompany.id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )

      if (!response.ok) {
        throw new Error('Failed to fetch invitations')
      }

      const data = await response.json()
      setInvitations(data.invitations || [])
    } catch (err) {
      console.error('Failed to fetch invitations:', err)
      setError(err instanceof Error ? err.message : 'Failed to load invitations')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateInvitation = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!newInvitation.contact.trim()) {
      setError('Please enter an email or phone number')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/invitations`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            contact: newInvitation.contact.trim(),
            type: newInvitation.type,
            role: newInvitation.role,
            team_id: currentCompany?.id
          })
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to create invitation')
      }

      // Reset form and refresh invitations
      setNewInvitation({ contact: '', type: 'email', role: 'MEMBER' })
      setShowCreateForm(false)
      await fetchInvitations()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invitation')
    } finally {
      setSubmitting(false)
    }
  }

  const generateInviteLink = (invitation: Invitation) => {
    const baseUrl = window.location.origin
    return `${baseUrl}/invite/${invitation.token}`
  }

  const copyInviteLink = async (invitation: Invitation) => {
    const link = generateInviteLink(invitation)
    try {
      await navigator.clipboard.writeText(link)
      setCopiedToken(invitation.id)
      setTimeout(() => setCopiedToken(null), 2000)
    } catch (err) {
      console.error('Failed to copy link:', err)
    }
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (invitation: Invitation) => {
    const now = Date.now() / 1000
    if (invitation.status === 'accepted') {
      return <Badge variant="success">Accepted</Badge>
    } else if (invitation.expires_at < now) {
      return <Badge variant="danger">Expired</Badge>
    } else {
      return <Badge variant="warning">Pending</Badge>
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="bg-white shadow rounded-lg">
            <div className="h-12 bg-gray-200 rounded-t-lg mb-2"></div>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 mb-1"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error && !showCreateForm) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load invitations</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button 
          onClick={fetchInvitations}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    )
  }

  if (!currentCompany) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Please select a company to manage invitations.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Team Invitations</h1>
          <p className="text-sm text-gray-600 mt-1">Invite new members to join {currentCompany.name}</p>
        </div>
        <Button 
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 text-white hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Send Invitation
        </Button>
      </div>

      {/* Create Invitation Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Send New Invitation</h2>
          
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleCreateInvitation} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact-type">Contact Method</Label>
                <Select
                  id="contact-type"
                  value={newInvitation.type}
                  onChange={(e) => setNewInvitation(prev => ({ ...prev, type: e.target.value as 'email' | 'phone' }))}
                  className="mt-1"
                >
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="role">Role</Label>
                <Select
                  id="role"
                  value={newInvitation.role}
                  onChange={(e) => setNewInvitation(prev => ({ ...prev, role: e.target.value }))}
                  className="mt-1"
                >
                  <option value="MEMBER">Member</option>
                  <option value="ADMIN">Admin</option>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="contact">
                {newInvitation.type === 'email' ? 'Email Address' : 'Phone Number'}
              </Label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {newInvitation.type === 'email' ? (
                    <Mail className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Phone className="h-5 w-5 text-gray-400" />
                  )}
                </div>
                <Input
                  id="contact"
                  type={newInvitation.type === 'email' ? 'email' : 'tel'}
                  value={newInvitation.contact}
                  onChange={(e) => setNewInvitation(prev => ({ ...prev, contact: e.target.value }))}
                  className="pl-10"
                  placeholder={newInvitation.type === 'email' ? 'user@example.com' : '+1 (555) 123-4567'}
                  required
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowCreateForm(false)
                  setError(null)
                  setNewInvitation({ contact: '', type: 'email', role: 'MEMBER' })
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                {submitting ? 'Sending...' : 'Send Invitation'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Invitations Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {invitations.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Mail className="w-12 h-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No invitations yet</h3>
            <p className="text-gray-600 mb-4">Start by sending your first team invitation.</p>
            <Button 
              onClick={() => setShowCreateForm(true)}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              Send Invitation
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contact</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Invited By</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invitations.map((invitation) => (
                <TableRow key={invitation.id}>
                  <TableCell className="font-medium">{invitation.contact}</TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      {invitation.contact_type === 'email' ? (
                        <Mail className="w-4 h-4 mr-1 text-gray-400" />
                      ) : (
                        <Phone className="w-4 h-4 mr-1 text-gray-400" />
                      )}
                      {invitation.contact_type}
                    </div>
                  </TableCell>
                  <TableCell>{invitation.role}</TableCell>
                  <TableCell>{getStatusBadge(invitation)}</TableCell>
                  <TableCell>{invitation.inviter_name}</TableCell>
                  <TableCell>{formatDate(invitation.created_at)}</TableCell>
                  <TableCell>
                    {invitation.status === 'pending' && invitation.expires_at > Date.now() / 1000 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyInviteLink(invitation)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        {copiedToken === invitation.id ? (
                          <>
                            <Check className="w-4 h-4 mr-1" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-1" />
                            Copy Link
                          </>
                        )}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}